/**
 * Аудиосессия Safari 16.4+ (`navigator.audioSession`, iOS и macOS).
 *
 * Тип `playback` нужен голосу Тико: после записи iOS оставляет сессию
 * «запись», и голос уходит в разговорный динамик — тихо. Но с типом
 * `playback` Safari отказывает в микрофоне: `getUserMedia` бросает
 * `InvalidStateError: AudioSession category is not compatible with audio
 * capture`. Поэтому:
 *   перед каждым открытием микрофона — `play-and-record` (синхронно, в касании);
 *   перед озвучкой — `playback`, но только если микрофон сейчас не открыт
 *   (иначе запись или перебивание голосом оборвались бы).
 * Где API нет (Chrome, Firefox, старый Safari), всё это ничего не делает.
 */

type AudioSessionType = 'auto' | 'playback' | 'transient' | 'transient-solo' | 'ambient' | 'play-and-record';

interface AudioSessionLike {
  type: AudioSessionType;
}

function session(): AudioSessionLike | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return (navigator as unknown as { readonly audioSession?: AudioSessionLike }).audioSession;
}

function setType(type: AudioSessionType): void {
  const current = session();
  if (current === undefined || current.type === type) return;
  try {
    current.type = type;
  } catch {
    // Смена запрещена — остаётся как есть.
  }
}

/** Открытые потоки микрофона: пока хоть один жив, сессию на `playback` не переводим. */
const captures = new Set<MediaStream>();

function capturing(): boolean {
  for (const stream of captures) {
    if (stream.getAudioTracks().some((track) => track.readyState === 'live')) return true;
    captures.delete(stream);
  }
  return false;
}

/** Текущий тип сессии — для диагностики; `null` — API нет. */
export function audioSessionType(): string | null {
  return session()?.type ?? null;
}

/** Перед `getUserMedia`: сессия, совместимая с записью. Звать синхронно, прямо перед запросом. */
export function prepareCaptureSession(): void {
  setType('play-and-record');
}

/**
 * Полный сброс аудиосессии (кнопка «Восстановить»): через `auto` обратно в
 * `play-and-record` — Safari перенастраивает сессию заново. Звать в касании.
 */
export function resetAudioSession(): void {
  setType('auto');
  setType('play-and-record');
}

/** Поток микрофона открыт — запомнить, чтобы озвучка не сменила сессию под ним. */
export function trackCapture(stream: MediaStream): MediaStream {
  captures.add(stream);
  return stream;
}

/** Перед озвучкой: громкий динамик, если микрофон сейчас не нужен. */
export function ensurePlaybackSession(): void {
  if (capturing()) return;
  setType('playback');
}
